/**
 * Background Location Tracking
 *
 * Uses expo-task-manager + expo-location to send GPS updates
 * even when the app is backgrounded or the phone is locked.
 * This is critical for multi-hour deliveries.
 */
import * as Location from 'expo-location';
import * as TaskManager from 'expo-task-manager';
import { api } from './api';

const TASK_NAME = 'COMMONPLACE_BACKGROUND_LOCATION';

// Register the background task at module level (required by expo-task-manager)
TaskManager.defineTask(TASK_NAME, async ({ data, error }: any) => {
  if (error) return;
  if (!data) return;

  const { locations } = data as { locations: Location.LocationObject[] };
  const jobId = await getActiveJobId();
  if (!jobId || !locations?.length) return;

  const latest = locations[locations.length - 1];
  try {
    await api.post('/tracking/update', {
      jobId,
      latitude: latest.coords.latitude,
      longitude: latest.coords.longitude,
      heading: latest.coords.heading,
      speed: latest.coords.speed,
    });
  } catch {
    // Silent fail — next update will come in 30s
  }
});

// Store active job ID in module scope for the background task
let _activeJobId: string | null = null;

function getActiveJobId(): Promise<string | null> {
  return Promise.resolve(_activeJobId);
}

/**
 * Start background location tracking for a job.
 * Call when driver starts EN_ROUTE_TO_SELLER or EN_ROUTE_TO_BUYER.
 */
export async function startBackgroundTracking(jobId: string): Promise<boolean> {
  _activeJobId = jobId;

  try {
    // Check/request permissions
    const { status: fgStatus } = await Location.requestForegroundPermissionsAsync();
    if (fgStatus !== 'granted') return false;

    const { status: bgStatus } = await Location.requestBackgroundPermissionsAsync();
    if (bgStatus !== 'granted') {
      // Background denied — foreground interval will still work
      return false;
    }

    // Check if already running
    const isRunning = await Location.hasStartedLocationUpdatesAsync(TASK_NAME).catch(() => false);
    if (isRunning) {
      // Already tracking — just update the job ID
      return true;
    }

    await Location.startLocationUpdatesAsync(TASK_NAME, {
      accuracy: Location.Accuracy.Balanced,
      timeInterval: 30000,        // Every 30 seconds
      distanceInterval: 50,        // Or every 50 meters
      deferredUpdatesInterval: 30000,
      showsBackgroundLocationIndicator: true,
      foregroundService: {
        notificationTitle: 'Commonplace Delivery',
        notificationBody: 'Tracking your location for live delivery updates',
        notificationColor: '#630E3D',
      },
    });

    return true;
  } catch {
    return false;
  }
}

/**
 * Stop background location tracking.
 * Call when job reaches COMPLETED, CANCELLED, or driver manually stops.
 */
export async function stopBackgroundTracking(): Promise<void> {
  _activeJobId = null;
  try {
    const isRunning = await Location.hasStartedLocationUpdatesAsync(TASK_NAME).catch(() => false);
    if (isRunning) {
      await Location.stopLocationUpdatesAsync(TASK_NAME);
    }
  } catch {
    // Silent fail
  }
}
